import {
    PYTHON_WORKER_CAPABILITIES,
    REALM_BOOTSTRAP_CAPABILITIES,
    createCryptographicId,
    createProtocolEnvelope,
    isCryptographicId,
    isProtocolEnvelope,
    missingCapabilities,
    requireExactWebOrigin,
} from "./pythonRealmProtocol.js";

export function configuredPythonRealmOrigin(
    configured = typeof import.meta !== "undefined"
        ? import.meta.env?.VITE_PYTHON_REALM_ORIGIN
        : undefined,
) {
    try {
        return requireExactWebOrigin(configured);
    } catch (error) {
        throw new PythonRealmRefusal("realm_origin_invalid", error);
    }
}

const DEFAULT_BOOT_TIMEOUT_MS = 120_000;
const DEFAULT_RUN_TIMEOUT_MS = 60_000;
// Serialized JSON UTF-16 code units, including the input wrapper. This stays
// deliberately generous for analytics results while bounding structured
// clone/parsing cost. The worker enforces the identical ceiling before send.
const MAX_JSON_CHARS = 16 * 1024 * 1024;
const MAX_WORKER_SOURCE_CHARS = 2 * 1024 * 1024;
const MAX_PYTHON_CODE_CHARS = 2 * 1024 * 1024;
// Bounds strings retained by the worker's serial run queue. This includes
// Python source plus serialized input JSON for the executing and waiting runs.
const MAX_OUTSTANDING_PAYLOAD_CHARS = 32 * 1024 * 1024;
const MAX_TIMER_MS = 2_147_483_647;
// Includes the worker's currently executing run and every waiting run.
// The 33rd outstanding run is refused before it is posted to the realm.
const MAX_OUTSTANDING_RUNS = 32;
// Matches the typed Python-step requirement ceiling. Each root still resolves
// through the pinned manifest and the aggregate wheel byte/count limits below.
const MAX_WHEEL_REQUESTS_PER_RUN = 8;
const MAX_ON_DEMAND_WHEELS = 8;
const MAX_ON_DEMAND_WHEEL_BYTES = 64 * 1024 * 1024;
const RUNTIME_FILE_NAME = /^[A-Za-z0-9][A-Za-z0-9._-]{0,254}$/;
const PACKAGE_NAME = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
const REALM_ERROR_REASON = /^[a-z][a-z0-9_]{0,127}$/;

export class PythonRealmRefusal extends Error {
    constructor(reason, cause) {
        super("Isolated Python is unavailable for this run.");
        this.name = "PythonRealmRefusal";
        this.code = "python_realm_unavailable";
        this.outcome = "refused";
        this.reason = reason;
        this.fallbackAllowed = false;
        if (cause !== undefined) {
            this.cause = cause;
            const diagnostic = typeof cause === "string"
                ? cause
                : typeof cause?.message === "string"
                    ? cause.message
                    : "";
            if (diagnostic) this.diagnostic = diagnostic.slice(0, 2_000);
        }
    }
}

class PythonRealmExecutionError extends Error {
    constructor(message, { outcome = "failed", reason = null } = {}) {
        super(message || "Python execution failed.");
        this.name = "PythonRealmExecutionError";
        this.outcome = ["refused", "cancelled"].includes(outcome)
            ? outcome
            : "failed";
        this.code = this.outcome === "refused"
            ? "python_execution_refused"
            : this.outcome === "cancelled"
                ? "python_execution_cancelled"
                : "python_execution_failed";
        this.reason = typeof reason === "string" && reason ? reason : null;
        this.refused = this.outcome === "refused";
        this.cancelled = this.outcome === "cancelled";
    }
}

function refusal(reason, cause) {
    return cause instanceof PythonRealmRefusal
        ? cause
        : new PythonRealmRefusal(reason, cause);
}

function requestRefusal(reason) {
    return new PythonRealmExecutionError(
        "The isolated Python run request is invalid.",
        { outcome: "refused", reason },
    );
}

function requestCancellation() {
    return new PythonRealmExecutionError(
        "Python execution was cancelled.",
        { outcome: "cancelled", reason: "run_cancelled" },
    );
}

function normalizeRequiredWheelPackages(value) {
    if (
        !Array.isArray(value)
        || value.length > MAX_WHEEL_REQUESTS_PER_RUN
    ) {
        return null;
    }
    const seen = new Set();
    const packages = [];
    for (const packageName of value) {
        if (
            typeof packageName !== "string"
            || !PACKAGE_NAME.test(packageName)
            || seen.has(packageName)
        ) {
            return null;
        }
        seen.add(packageName);
        packages.push(packageName);
    }
    return packages;
}

function packageScopeContains(packageScope, requiredPackages) {
    return requiredPackages.every((packageName) => packageScope.includes(packageName));
}

function cloneRuntimeFiles(runtimeFiles) {
    if (!runtimeFiles || typeof runtimeFiles !== "object" || Array.isArray(runtimeFiles)) {
        throw refusal("runtime_assets_invalid");
    }

    const files = Object.create(null);
    const transfer = [];
    const entries = Object.entries(runtimeFiles);
    if (entries.length === 0) {
        throw refusal("runtime_assets_invalid");
    }

    for (const [name, value] of entries) {
        if (!RUNTIME_FILE_NAME.test(name)) {
            throw refusal("runtime_assets_invalid");
        }

        let bytes;
        if (value instanceof ArrayBuffer) {
            bytes = value.slice(0);
        } else if (ArrayBuffer.isView(value) && value.buffer instanceof ArrayBuffer) {
            bytes = value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength);
        } else {
            throw refusal("runtime_assets_invalid");
        }

        Object.defineProperty(files, name, {
            value: bytes,
            enumerable: true,
            configurable: false,
            writable: false,
        });
        transfer.push(bytes);
    }

    return { files, transfer };
}

function stringifyJson(value, reason, {
    limitReason = null,
    limitMessage = null,
} = {}) {
    let json;
    try {
        json = JSON.stringify(value);
    } catch {
        throw requestRefusal(reason);
    }
    if (typeof json !== "string") {
        throw requestRefusal(reason);
    }
    if (json.length > MAX_JSON_CHARS) {
        if (limitReason) {
            throw new PythonRealmExecutionError(limitMessage, {
                outcome: "refused",
                reason: limitReason,
            });
        }
        throw requestRefusal(reason);
    }
    return json;
}

function freezeJsonSnapshot(value) {
    const json = stringifyJson(value, "run_attribution_invalid");
    const copy = JSON.parse(json);
    const stack = [copy];
    while (stack.length > 0) {
        const current = stack.pop();
        if (!current || typeof current !== "object" || Object.isFrozen(current)) continue;
        Object.freeze(current);
        for (const child of Object.values(current)) stack.push(child);
    }
    return copy;
}

function normalizeWheelPayload(payload) {
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
    if (typeof payload.packageName !== "string" || !PACKAGE_NAME.test(payload.packageName)) {
        return null;
    }
    if (payload.version !== undefined && (
        typeof payload.version !== "string"
        || !payload.version
        || payload.version.length > 128
    )) {
        return null;
    }
    if (payload.reason !== undefined && (
        typeof payload.reason !== "string"
        || payload.reason.length > 1_000
    )) {
        return null;
    }
    if (
        !Array.isArray(payload.missingPackages)
        || payload.missingPackages.length > MAX_ON_DEMAND_WHEELS
        || new Set(payload.missingPackages).size !== payload.missingPackages.length
        || payload.missingPackages.some((name) => (
            typeof name !== "string" || !PACKAGE_NAME.test(name)
        ))
    ) {
        return null;
    }
    if (
        payload.reuseInstalled !== undefined
        && payload.reuseInstalled !== true
    ) {
        return null;
    }

    return {
        packageName: payload.packageName,
        missingPackages: [...payload.missingPackages],
        ...(payload.version === undefined ? {} : { version: payload.version }),
        ...(payload.reuseInstalled === true ? { reuseInstalled: true } : {}),
        ...(payload.reason === undefined ? {} : { reason: payload.reason }),
    };
}

function wheelRefusal() {
    return {
        ok: false,
        outcome: "refused",
        refusal: {
            reason: "wheel_request_not_authorized",
            message: "This run is not allowed to install that package.",
        },
        error: "wheel request refused",
    };
}

function normalizeExecutionFailure(message) {
    if (
        !message
        || message.ok !== false
        || typeof message.error !== "string"
        || message.error.length === 0
        || message.error.length > 32_768
    ) {
        return null;
    }
    if (message.outcome === undefined) {
        return { outcome: "failed", reason: null };
    }
    if (
        message.outcome !== "refused"
        || !message.refusal
        || typeof message.refusal !== "object"
        || Array.isArray(message.refusal)
        || typeof message.refusal.reason !== "string"
        || message.refusal.reason.length === 0
        || message.refusal.reason.length > 256
        || typeof message.refusal.message !== "string"
        || message.refusal.message.length === 0
        || message.refusal.message.length > 2_000
    ) {
        return null;
    }
    return {
        outcome: "refused",
        reason: message.refusal.reason,
    };
}

function normalizeWheelResponse(candidate, request) {
    if (
        candidate?.ok === true
        && candidate.approved === true
        && candidate.packageName === request.packageName
        && PACKAGE_NAME.test(candidate.packageName)
        && typeof candidate.version === "string"
        && candidate.version.length > 0
        && candidate.version.length <= 128
        && (request.version === undefined || candidate.version === request.version)
        && Array.isArray(candidate.wheels)
        && candidate.wheels.length <= MAX_ON_DEMAND_WHEELS
    ) {
        if (request.reuseInstalled === true) {
            if (
                candidate.reuseInstalled !== true
                || candidate.wheels.length !== 0
            ) {
                return { payload: wheelRefusal(), transfer: [] };
            }
            return {
                payload: {
                    ok: true,
                    approved: true,
                    packageName: candidate.packageName,
                    version: candidate.version,
                    reuseInstalled: true,
                    wheels: [],
                },
                transfer: [],
            };
        }
        if (
            candidate.reuseInstalled !== undefined
            || candidate.wheels.length === 0
        ) {
            return { payload: wheelRefusal(), transfer: [] };
        }
        const seenPackages = new Set();
        const seenFiles = new Set();
        const seenBuffers = new Set();
        const wheels = [];
        const transfer = [];
        let totalBytes = 0;
        for (const wheel of candidate.wheels) {
            if (
                !wheel
                || typeof wheel !== "object"
                || !PACKAGE_NAME.test(wheel.packageName || "")
                || !RUNTIME_FILE_NAME.test(wheel.fileName || "")
                || typeof wheel.version !== "string"
                || !wheel.version
                || wheel.version.length > 128
                || !(wheel.bytes instanceof ArrayBuffer)
                || wheel.bytes.byteLength === 0
                || seenPackages.has(wheel.packageName)
                || seenFiles.has(wheel.fileName)
                || seenBuffers.has(wheel.bytes)
            ) {
                return { payload: wheelRefusal(), transfer: [] };
            }
            totalBytes += wheel.bytes.byteLength;
            if (totalBytes > MAX_ON_DEMAND_WHEEL_BYTES) {
                return { payload: wheelRefusal(), transfer: [] };
            }
            seenPackages.add(wheel.packageName);
            seenFiles.add(wheel.fileName);
            seenBuffers.add(wheel.bytes);
            wheels.push({
                packageName: wheel.packageName,
                version: wheel.version,
                fileName: wheel.fileName,
                bytes: wheel.bytes,
            });
            transfer.push(wheel.bytes);
        }
        const rootWheel = wheels.find(
            (wheel) => wheel.packageName === candidate.packageName,
        );
        if (!rootWheel || rootWheel.version !== candidate.version) {
            return { payload: wheelRefusal(), transfer: [] };
        }
        return {
            payload: {
                ok: true,
                approved: true,
                packageName: candidate.packageName,
                version: candidate.version,
                wheels,
            },
            transfer,
        };
    }

    const fallback = wheelRefusal();
    if (candidate?.ok !== false || candidate.outcome !== "refused") {
        return { payload: fallback, transfer: [] };
    }
    const reason = typeof candidate.refusal?.reason === "string"
        && candidate.refusal.reason.length <= 256
        ? candidate.refusal.reason
        : fallback.refusal.reason;
    const message = typeof candidate.refusal?.message === "string"
        && candidate.refusal.message.length <= 2_000
        ? candidate.refusal.message
        : fallback.refusal.message;
    const error = typeof candidate.error === "string"
        && candidate.error.length <= 2_000
        ? candidate.error
        : "wheel request refused";
    return {
        payload: {
            ok: false,
            outcome: "refused",
            refusal: { reason, message },
            error,
        },
        transfer: [],
    };
}

const controllerPackageScopes = new WeakMap();

class PythonRealmController {
    constructor({
        origin,
        workerSource,
        runtimeFiles,
        packageScope,
        windowRef,
        documentRef,
        cryptoImpl,
        bootTimeoutMs,
        runTimeoutMs,
        setTimeoutImpl,
        clearTimeoutImpl,
        onRealmTeardown,
    }) {
        this.origin = origin;
        this._workerSource = workerSource;
        controllerPackageScopes.set(this, Object.freeze([...packageScope]));
        // Keep only the one boot-transfer copy. Once transferred to the realm
        // these buffers are detached and the reference is dropped.
        this._runtime = cloneRuntimeFiles(runtimeFiles);
        this._window = windowRef;
        this._document = documentRef;
        this._crypto = cryptoImpl;
        this._bootTimeoutMs = bootTimeoutMs;
        this._runTimeoutMs = runTimeoutMs;
        this._setTimeout = setTimeoutImpl;
        this._clearTimeout = clearTimeoutImpl;
        this._onRealmTeardown = onRealmTeardown;
        this._channelId = createCryptographicId(cryptoImpl);
        this._state = "created";
        this.generation = 0;
        this._realmReadySeen = false;
        this._workerReadySeen = false;
        this._inFlight = new Map();
        this._inFlightPayloadChars = 0;
        this._frame = null;
        this._bootTimer = null;
        this._terminalError = null;
        this._onMessage = (event) => this._handleMessage(event);
        this._createReadyPromise();
    }

    _createReadyPromise() {
        this._readyPromise = new Promise((resolve, reject) => {
            this._resolveReady = resolve;
            this._rejectReady = reject;
        });
    }

    async start() {
        if (this._state !== "created") {
            throw refusal("controller_state_invalid");
        }
        await this._bootFrame();
        return this;
    }

    async _bootFrame() {
        const parentOrigin = requireExactWebOrigin(this._window?.location?.origin);
        if (parentOrigin === this.origin) {
            throw refusal("realm_is_not_cross_origin");
        }
        if (!this._document?.body || typeof this._document.createElement !== "function") {
            throw refusal("document_unavailable");
        }

        const realmUrl = new URL("/realm.html", `${this.origin}/`);
        realmUrl.searchParams.set("parentOrigin", parentOrigin);
        realmUrl.searchParams.set("channelId", this._channelId);

        const frame = this._document.createElement("iframe");
        frame.setAttribute("sandbox", "allow-scripts allow-same-origin");
        frame.setAttribute("aria-hidden", "true");
        frame.setAttribute("referrerpolicy", "no-referrer");
        frame.style.display = "none";
        frame.src = realmUrl.href;
        frame.onerror = () => this._fail(refusal("bootstrap_failed"));
        this._frame = frame;
        if (!this._runtime) {
            throw refusal("runtime_assets_reload_unavailable");
        }
        this._state = "booting";

        this._window.addEventListener("message", this._onMessage);
        this._document.body.appendChild(frame);
        this._bootTimer = this._setTimeout(
            () => this._fail(refusal("bootstrap_timeout")),
            this._bootTimeoutMs,
        );

        await this._readyPromise;
    }

    runPython({
        code,
        inputs = {},
        params = {},
        globals,
        attribution,
        onWheelRequest,
        timeoutMs = this._runTimeoutMs,
        signal,
        onLaunched,
        runId,
        requiredWheelPackages,
    } = {}) {
        if (this._state !== "ready") {
            return Promise.reject(refusal("realm_not_ready"));
        }
        if (this._inFlight.size >= MAX_OUTSTANDING_RUNS) {
            return Promise.reject(new PythonRealmExecutionError(
                "The isolated Python run queue is full.",
                {
                    outcome: "refused",
                    reason: "realm_run_limit_exceeded",
                },
            ));
        }
        if (typeof code !== "string") {
            return Promise.reject(requestRefusal("run_request_invalid"));
        }
        if (code.length > MAX_PYTHON_CODE_CHARS) {
            return Promise.reject(new PythonRealmExecutionError(
                "Python source exceeds the 2 Mi-character limit.",
                {
                    outcome: "refused",
                    reason: "realm_code_limit_exceeded",
                },
            ));
        }
        if (!attribution || typeof attribution !== "object" || Array.isArray(attribution)) {
            return Promise.reject(requestRefusal("run_attribution_invalid"));
        }
        if (onWheelRequest !== undefined && typeof onWheelRequest !== "function") {
            return Promise.reject(requestRefusal("run_request_invalid"));
        }
        const normalizedRequiredWheelPackages = normalizeRequiredWheelPackages(
            requiredWheelPackages === undefined
                ? controllerPackageScopes.get(this)
                : requiredWheelPackages,
        );
        const packageScope = controllerPackageScopes.get(this);
        if (
            normalizedRequiredWheelPackages === null
            || !packageScopeContains(packageScope, normalizedRequiredWheelPackages)
        ) {
            return Promise.reject(requestRefusal("run_request_invalid"));
        }
        if (runId !== undefined && (
            typeof runId !== "string"
            || !runId
            || runId.length > 256
        )) {
            return Promise.reject(requestRefusal("run_request_invalid"));
        }
        if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
            return Promise.reject(requestRefusal("run_request_invalid"));
        }

        let inputsJson;
        let attributionSnapshot;
        try {
            const globalValues = globals === undefined
                ? { inputs, params }
                : globals;
            if (
                !globalValues
                || typeof globalValues !== "object"
                || Array.isArray(globalValues)
            ) {
                return Promise.reject(requestRefusal("run_input_invalid"));
            }
            inputsJson = stringifyJson({
                globals: globalValues,
            }, "run_input_invalid", {
                limitReason: "realm_input_limit_exceeded",
                limitMessage: "Python input JSON exceeds the 16 Mi-character limit.",
            });
            attributionSnapshot = freezeJsonSnapshot(attribution);
        } catch (error) {
            return Promise.reject(error);
        }
        const payloadChars = code.length + inputsJson.length;
        if (
            payloadChars > MAX_OUTSTANDING_PAYLOAD_CHARS
            || this._inFlightPayloadChars
                > MAX_OUTSTANDING_PAYLOAD_CHARS - payloadChars
        ) {
            return Promise.reject(new PythonRealmExecutionError(
                "The isolated Python payload queue is full.",
                {
                    outcome: "refused",
                    reason: "realm_payload_capacity_exceeded",
                },
            ));
        }

        let id;
        try {
            for (let attempt = 0; attempt < 8; attempt += 1) {
                const candidate = createCryptographicId(this._crypto);
                if (!this._inFlight.has(candidate)) {
                    id = candidate;
                    break;
                }
            }
        } catch (error) {
            return Promise.reject(refusal("cryptographic_randomness_unavailable", error));
        }
        if (!id) return Promise.reject(refusal("run_id_collision"));

        return new Promise((resolve, reject) => {
            const record = {
                attribution: attributionSnapshot,
                externalRunId: runId,
                onWheelRequest,
                wheelRequestIds: new Set(),
                resolve,
                reject,
                timer: null,
                abort: null,
                signal: null,
                payloadChars,
                posted: false,
                requestSettled: false,
            };
            record.timer = this._setTimeout(
                () => this._fail(refusal("run_timeout"), {
                    triggerRunId: record.externalRunId || id,
                    triggerReason: "run_timeout",
                }),
                Math.min(timeoutMs, MAX_TIMER_MS),
            );
            if (signal) {
                if (signal.aborted) {
                    this._clearTimeout(record.timer);
                    reject(requestCancellation());
                    return;
                }
                record.abort = () => this._cancelRequest(id, record);
                record.signal = signal;
                signal.addEventListener("abort", record.abort, { once: true });
            }

            this._inFlightPayloadChars += payloadChars;
            this._inFlight.set(id, record);
            try {
                onLaunched?.({
                    id,
                    runId,
                    cancel: () => this._cancelRequest(id, record),
                });
                if (this._state !== "ready" || this._inFlight.get(id) !== record) return;
                this._post(createProtocolEnvelope(this._channelId, "run", {
                    id,
                    code,
                    inputsJson,
                    // Derived exclusively from the parent-side run record and
                    // retained in the worker protocol for policy auditing.
                    // Cached roots are reauthorized before every run; this
                    // bit cannot grant authority without the record callback.
                    wheelAuthorityEligible: typeof onWheelRequest === "function",
                    packageScope,
                    requiredWheelPackages: normalizedRequiredWheelPackages,
                }));
                record.posted = true;
            } catch (error) {
                this._fail(refusal("message_delivery_failed", error));
            }
        });
    }

    destroy() {
        this._fail(refusal("realm_destroyed"));
    }

    terminateRunRealm({ runId, reason = "run_cancelled" } = {}) {
        const cancelledRunIds = [];
        for (const [transportId, record] of this._inFlight) {
            cancelledRunIds.push(record.externalRunId || transportId);
        }
        const matched = runId === undefined || cancelledRunIds.includes(runId);
        if (matched && this._state !== "failed") {
            // The adapter that requested this teardown publishes the returned
            // sibling ids with its trigger context. Suppress the controller's
            // internal-failure notification so listeners see one event.
            this._fail(refusal(reason), { reportTeardown: false });
        }
        return {
            terminated: matched,
            cancelledRunIds: matched ? cancelledRunIds : [],
        };
    }

    whenReady() {
        if (this._state === "ready") return Promise.resolve(this);
        if (this._state === "booting") {
            return this._readyPromise.then(() => this);
        }
        return Promise.reject(refusal("realm_not_ready"));
    }

    _handleMessage(event) {
        if (this._state === "failed") return;
        const frameWindow = this._frame?.contentWindow;
        if (!frameWindow || event.source !== frameWindow) return;
        if (event.origin !== this.origin) {
            this._fail(refusal("message_origin_mismatch"));
            return;
        }

        const message = event.data;
        if (!isProtocolEnvelope(message, this._channelId)) {
            this._fail(refusal("protocol_mismatch"));
            return;
        }

        switch (message.type) {
            case "realm-ready":
                this._handleRealmReady(message);
                break;
            case "worker-ready":
                this._handleWorkerReady(message);
                break;
            case "realm-error":
                if (
                    !REALM_ERROR_REASON.test(message.reason || "")
                    || (
                        message.diagnostic !== undefined
                        && (
                            typeof message.diagnostic !== "string"
                            || !message.diagnostic
                            || message.diagnostic.length > 2_000
                        )
                    )
                ) {
                    this._fail(refusal("protocol_mismatch"));
                    break;
                }
                this._fail(refusal(
                    message.reason,
                    message.diagnostic
                        ? new Error(message.diagnostic)
                        : undefined,
                ));
                break;
            case "result":
                this._handleResult(message);
                break;
            case "wheelRequest":
                void this._handleWheelRequest(message);
                break;
            default:
                this._fail(refusal("protocol_mismatch"));
        }
    }

    _handleRealmReady(message) {
        if (this._state !== "booting" || this._realmReadySeen) {
            this._fail(refusal("duplicate_realm_ready"));
            return;
        }
        this._realmReadySeen = true;
        if (missingCapabilities(
            message.capabilities,
            REALM_BOOTSTRAP_CAPABILITIES,
        ).length > 0) {
            this._fail(refusal("capability_mismatch"));
            return;
        }

        const { files, transfer } = this._runtime;
        try {
            this._post(createProtocolEnvelope(this._channelId, "initialize", {
                workerSource: this._workerSource,
                files,
            }), transfer);
            this._runtime = null;
        } catch (error) {
            this._fail(refusal("message_delivery_failed", error));
        }
    }

    _handleWorkerReady(message) {
        if (
            this._state !== "booting"
            || !this._realmReadySeen
            || this._workerReadySeen
        ) {
            this._fail(refusal("protocol_mismatch"));
            return;
        }
        this._workerReadySeen = true;
        if (missingCapabilities(
            message.capabilities,
            PYTHON_WORKER_CAPABILITIES,
        ).length > 0) {
            this._fail(refusal("capability_mismatch"));
            return;
        }

        this._state = "ready";
        this._clearTimeout(this._bootTimer);
        this._bootTimer = null;
        this._resolveReady();
    }

    _handleResult(message) {
        if (this._state !== "ready") {
            this._fail(refusal("protocol_mismatch"));
            return;
        }

        const record = this._inFlight.get(message.id);
        if (!record) return;
        if (!isCryptographicId(message.id)) {
            this._fail(refusal("protocol_mismatch"));
            return;
        }
        this._inFlight.delete(message.id);
        this._cleanRunRecord(record);
        if (record.requestSettled) return;

        if (message.ok === true && typeof message.resultJson === "string") {
            if (message.resultJson.length > MAX_JSON_CHARS) {
                record.reject(refusal("result_invalid"));
                this._fail(refusal("protocol_mismatch"));
                return;
            }
            try {
                record.resolve(JSON.parse(message.resultJson));
            } catch (error) {
                record.reject(refusal("result_invalid", error));
                this._fail(refusal("protocol_mismatch"));
            }
            return;
        }

        const failure = normalizeExecutionFailure(message);
        if (failure) {
            record.reject(new PythonRealmExecutionError(message.error, {
                outcome: failure.outcome,
                reason: failure.reason,
            }));
            return;
        }

        record.reject(refusal("result_invalid"));
        this._fail(refusal("protocol_mismatch"));
    }

    async _handleWheelRequest(message) {
        if (this._state !== "ready") {
            this._fail(refusal("protocol_mismatch"));
            return;
        }

        const record = this._inFlight.get(message.id);
        if (!record) return;
        if (!isCryptographicId(message.id)) {
            this._fail(refusal("protocol_mismatch"));
            return;
        }
        if (
            !isCryptographicId(message.requestId)
            || record.wheelRequestIds.has(message.requestId)
            || record.wheelRequestIds.size >= MAX_WHEEL_REQUESTS_PER_RUN
        ) {
            this._fail(refusal("protocol_mismatch"));
            return;
        }

        const payload = normalizeWheelPayload(message.payload);
        if (!payload) {
            this._fail(refusal("protocol_mismatch"));
            return;
        }
        record.wheelRequestIds.add(message.requestId);

        let response = { payload: wheelRefusal(), transfer: [] };
        if (!record.requestSettled && record.onWheelRequest) {
            try {
                const candidate = await record.onWheelRequest({
                    ...payload,
                    attribution: record.attribution,
                    runId: message.id,
                    requestId: message.requestId,
                });
                response = normalizeWheelResponse(candidate, payload);
            } catch {
                response = { payload: wheelRefusal(), transfer: [] };
            }
        }

        if (this._inFlight.get(message.id) !== record || this._state !== "ready") {
            return;
        }
        try {
            this._post(createProtocolEnvelope(this._channelId, "wheelRequestResult", {
                id: message.id,
                requestId: message.requestId,
                payload: response.payload,
            }), response.transfer);
        } catch (error) {
            this._fail(refusal("message_delivery_failed", error));
        }
    }

    _post(message, transfer = []) {
        const frameWindow = this._frame?.contentWindow;
        if (!frameWindow) throw new Error("realm frame is unavailable");
        frameWindow.postMessage(message, this.origin, transfer);
    }

    _detachFrame() {
        this._window?.removeEventListener?.("message", this._onMessage);
        this._frame?.remove?.();
        this._frame = null;
        this._clearTimeout(this._bootTimer);
        this._bootTimer = null;
    }

    _cleanRunRecord(record) {
        this._clearTimeout(record.timer);
        if (record.payloadChars > 0) {
            this._inFlightPayloadChars = Math.max(
                0,
                this._inFlightPayloadChars - record.payloadChars,
            );
            record.payloadChars = 0;
        }
        if (record.abort && record.signal) {
            record.signal.removeEventListener("abort", record.abort);
        }
        record.abort = null;
        record.signal = null;
    }

    _cancelRequest(id, record) {
        if (
            this._inFlight.get(id) !== record
            || record.requestSettled
        ) {
            return false;
        }
        record.requestSettled = true;
        if (record.abort && record.signal) {
            record.signal.removeEventListener("abort", record.abort);
        }
        record.abort = null;
        record.signal = null;
        record.reject(requestCancellation());

        // A synchronous onLaunched cancellation happens before the worker sees
        // the request, so there is no completion frame to retain a tombstone
        // for. Once posted, keep the record (and its deadline/capacity charge)
        // until the worker replies. A stuck cancelled request still expires
        // the realm, while an ordinary late reply is simply ignored.
        if (!record.posted) {
            this._inFlight.delete(id);
            this._cleanRunRecord(record);
        }
        return true;
    }

    _fail(error, {
        reportTeardown = true,
        triggerRunId = null,
        triggerReason = error?.reason || null,
    } = {}) {
        if (this._state === "failed") return;
        const wasBooting = this._state === "booting";
        const cancelledRunIds = [
            ...new Set([...this._inFlight.entries()]
                .filter(([, record]) => !record.requestSettled)
                .map(([transportId, record]) => record.externalRunId || transportId)),
        ];
        this._terminalError = error;
        this._state = "failed";
        this._clearTimeout(this._bootTimer);
        this._bootTimer = null;
        this._runtime = null;
        this._detachFrame();

        if (wasBooting) this._rejectReady(error);
        if (
            reportTeardown
            && cancelledRunIds.length > 0
            && typeof this._onRealmTeardown === "function"
        ) {
            try {
                this._onRealmTeardown({
                    cancelledRunIds,
                    triggerRunId,
                    triggerReason,
                });
            } catch {
                // Reporting cannot prevent deterministic realm cleanup.
            }
        }
        for (const record of this._inFlight.values()) {
            this._cleanRunRecord(record);
            if (!record.requestSettled) record.reject(error);
        }
        this._inFlight.clear();
    }
}

export async function createPythonRealmController({
    origin = configuredPythonRealmOrigin(),
    workerSource,
    runtimeFiles,
    packageScope = [],
    bootTimeoutMs = DEFAULT_BOOT_TIMEOUT_MS,
    runTimeoutMs = DEFAULT_RUN_TIMEOUT_MS,
    onRealmTeardown,
    windowRef = globalThis,
    documentRef = globalThis.document,
    cryptoImpl = globalThis.crypto,
    setTimeoutImpl = globalThis.setTimeout.bind(globalThis),
    clearTimeoutImpl = globalThis.clearTimeout.bind(globalThis),
} = {}) {
    if (
        typeof workerSource !== "string"
        || !workerSource
        || workerSource.length > MAX_WORKER_SOURCE_CHARS
    ) {
        throw refusal("worker_source_invalid");
    }
    const normalizedPackageScope = normalizeRequiredWheelPackages(packageScope);
    if (normalizedPackageScope === null) {
        throw refusal("package_scope_invalid");
    }
    if (!Number.isFinite(bootTimeoutMs) || bootTimeoutMs <= 0) {
        throw refusal("bootstrap_configuration_invalid");
    }
    if (!Number.isFinite(runTimeoutMs) || runTimeoutMs <= 0) {
        throw refusal("run_configuration_invalid");
    }
    if (onRealmTeardown !== undefined && typeof onRealmTeardown !== "function") {
        throw refusal("teardown_configuration_invalid");
    }

    let normalizedOrigin;
    try {
        normalizedOrigin = requireExactWebOrigin(origin);
    } catch (error) {
        throw refusal("realm_origin_invalid", error);
    }

    let controller;
    try {
        controller = new PythonRealmController({
            origin: normalizedOrigin,
            workerSource,
            runtimeFiles,
            packageScope: normalizedPackageScope,
            windowRef,
            documentRef,
            cryptoImpl,
            bootTimeoutMs,
            runTimeoutMs,
            setTimeoutImpl,
            clearTimeoutImpl,
            onRealmTeardown,
        });
        return await controller.start();
    } catch (error) {
        controller?.destroy();
        throw refusal(error?.reason || "bootstrap_failed", error);
    }
}
